// File: frontend/src/components/PublicChatWidget.js
// Версия: 25.8 (Hotfix: Скрыт раздел Иммиграция, структура сохранена)
// Дата: 17 декабря 2025
// Описание:
// - [ЗАДАЧА 42 / v25.7] РЕШЕНИЕ: Номер в чате не форматировался.
//   1. Импортирован 'react-input-mask'.
//   2. Добавлена логика (isPhoneInput) для определения, когда бот ждет номер телефона.
//   3. В JSX-форме обычный <input> заменен на <InputMask mask="+1 (999) 999-9999">,
//      который включается только на этапе ввода телефона.
// - (Сохранено из v25.6) Все исправления для кнопок (ЗАДАЧА 41) сохранены.

// --- 1. ИМПОРТЫ ---
import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import apiService from '../services/apiService';
import { useBranding } from '../context/BrandingContext';
import './PublicChatWidget.css';
// --- [НОВАЯ ФУНКЦИЯ v25.0] ---
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css'; // Импортируем стили календаря
// Для форматирования даты
import { format as formatDate } from 'date-fns';
// --- [ИЗМЕНЕНИЕ v25.7] ---
import InputMask from 'react-input-mask';
// --- [КОНЕЦ ИЗМЕНЕНИЯ v25.7] ---
import { razmetkaVHtml } from '../utils/razmetka';


// Resolve parent origin from referrer for secure postMessage targeting.
// Falls back to '*' only if referrer is unavailable (direct iframe load).
const _getParentOrigin = () => {
    try {
        if (document.referrer) return new URL(document.referrer).origin;
    } catch (_) {}
    return '*';
};
const PARENT_ORIGIN = _getParentOrigin();

// --- (Шаг 3.1): Helper для отправки сообщений ---
const sendMessageToParent = (type, payload = {}) => {
    if (window.parent && window.parent !== window) {
        try {
            window.parent.postMessage({ type, payload }, PARENT_ORIGIN);
        } catch (error) {
            console.error('[Widget] Failed to send message to parent:', error);
        }
    }
};

// --- 2. СТРОКИ И ПЕРЕВОДЫ ---
const translations = {
    en: {
        operatorName: ['Beth', 'Jessica', 'Michael', 'David', 'Lindsay'][Math.floor(Math.random() * 5)],
        greeting: "Hi, I am {operatorName}. To start, please select your preferred language.",
        transition_message: "One moment, connecting you to {operatorName}...",
        welcome: "Welcome! I'm {operatorName}. How can I assist you today?",
        askService: "Which legal area is your question related to?",
        services: {
            // immigration: "Immigration", // --- [СКРЫТО v25.8] ---
            estate_planning: "Estate Planning",
            real_estate: "Real Estate",
            other: "Something Else"
        },
        // --- [СКРЫТО v25.8] ---
        // askImmigrationTopic: "Understood. Which of our immigration services are you interested in?",
        // immigrationTopics: {
        //     investor_visa: "Investor Visas (E-2, EB-5)",
        //     employment_visa: "Employment-Based Visas (L-1, O-1, H-1B)",
        //     family_green_card: "Family-Based Green Cards",
        //     fiance_visa: "Fiancé(e) Visas (K-1)",
        //     adjustment_status: "Adjustment of Status",
        //     naturalization: "Naturalization & Citizenship"
        // },
        // ---------------------
        askEstateTopic: "Certainly. Are you primarily interested in creating a Will or a Trust?",
        estateTopics: {
            will: "Will",
            trust: "Trust",
            unsure: "I'm not sure"
        },
        askRealEstateTopic: "Of course. What aspect of real estate can I help you with today?",
        realEstateTopics: {
            purchase_sale: "Purchase and Sale Agreements",
            title_insurance: "Title Examination & Insurance",
            closing_services: "Closing & Settlement Services",
            fsbo: "For Sale By Owner (FSBO)",
            lease_agreements: "Lease Agreements",
            international_clients: "Counsel for International Clients"
        },
        inquiryThanks: "Thank you for clarifying. To properly assist you, I just need to collect a few details.",
        askName: "First, what is your full name?",
        askEmail: "Thank you, {name}. What is your email address?",
        askPhone: "Got it. And finally, what is your phone number?",
        thanksAndNextSteps: "Great, {name}. I have your initial details. What would you like to do next?",
        nextSteps: {
            consultation: { text: "Schedule Consultation Now", style: "primary", value: "consultation" },
            email_questions: { text: "Email Me a List of Questions", style: "secondary", value: "email_questions" },
            chat_here: { text: "Ask More Questions Here", style: "tertiary", value: "chat_here" }
        },
        // --- [НОВАЯ ФУНКЦИЯ v25.0] ---
        // Фраза-триггер для календаря (должна совпадать с будущим промптом v1.30)
        askDatePrompt: "Great! To help you schedule a consultation, could you please let me know what date you would prefer? Please select a date from the calendar below or type it in.",
        dateSelectedConfirmation: "Date selected: {date}", // Сообщение пользователя после выбора даты
        // --- [КОНЕЦ ИЗМЕНЕНИЯ v25.0] ---
        ai_transition: "Perfect. You can now ask your legal questions.",

        // --- [UX ИСПРАВЛЕНИЕ v25.3] ---
        finalMessage: "Thank you! Our team will review your information and reach out to you shortly. Have a great day!",
        // --- [КОНЕЦ ИСПРАВЛЕНИЯ v25.3] ---

        validation: {
            invalid_name: "That doesn't seem to be a valid name. Please enter your full name.",
            invalid_email: "This email address appears to be invalid. Please check and re-enter it.",
            invalid_phone: "This phone number appears to be invalid. Please enter a valid number.",
            ai_error: "I'm sorry, I'm having trouble connecting right now. Please try again later.",
            placeholder_text: "Type your answer here...",
            // --- [ИЗМЕНЕНИЕ v25.7] ---
            placeholder_phone: "+1 (___) ___-____"
            // --- [КОНЕЦ ИЗМЕНЕНИЯ v25.7] ---
        }
    },
    ru: {
        operatorName: ['Анна', 'Дмитрий', 'Светлана', 'Алексей', 'Екатерина'][Math.floor(Math.random() * 5)],
        greeting: "Здравствуйте, я {operatorName}. Для начала, пожалуйста, выберите предпочитаемый язык.",
        transition_message: "One moment, please. I'm connecting you to a specialist.",
        welcome: "Добро пожаловать! Я {operatorName}. Чем могу вам помочь?",
        askService: "К какой области права относится ваш вопрос?",
        services: {
            // immigration: "Иммиграция", // --- [СКРЫТО v25.8] ---
            estate_planning: "Планирование наследства",
            real_estate: "Недвижимость",
            other: "Что-то другое"
        },
        // --- [СКРЫТО v25.8] ---
        // askImmigrationTopic: "Понятно. Какие из наших иммиграционных услуг вас интересуют?",
        // immigrationTopics: {
        //     investor_visa: "Инвесторские визы (E-2, EB-5)",
        //     employment_visa: "Рабочие визы (L-1, O-1, H-1B)",
        //     family_green_card: "Воссоединение семьи (Грин-карты)",
        //     fiance_visa: "Визы жениха/невесты (K-1)",
        //     adjustment_status: "Смена статуса (AOS)",
        //     naturalization: "Натурализация и гражданство"
        // },
        // ---------------------
        askEstateTopic: "Конечно. Вас в первую очередь интересует составление завещания или траста?",
        estateTopics: {
            will: "Завещание",
            trust: "Траст",
            unsure: "Я не уверен(а)"
        },
        askRealEstateTopic: "Разумеется. С каким аспектом недвижимости я могу вам помочь?",
        realEstateTopics: {
            purchase_sale: "Договоры купли-продажи",
            title_insurance: "Проверка права собственности и страхование",
            closing_services: "Услуги по закрытию сделки",
            fsbo: "Продажа владельцем (FSBO)",
            lease_agreements: "Договоры аренды",
            international_clients: "Консультации для иностранных клиентов"
        },
        inquiryThanks: "Спасибо за уточнение. Чтобы вам помочь, мне нужно собрать несколько деталей.",
        askName: "Во-первых, как ваше полное имя?",
        askEmail: "Спасибо, {name}. Какой у вас адрес электронной почты?",
        askPhone: "Хорошо. И, наконец, какой у вас номер телефона?",
        thanksAndNextSteps: "Отлично, {name}. Я записал(а) ваши данные. Чем могу помочь дальше?",
        nextSteps: {
            consultation: { text: "Записаться на консультацию", style: "primary", value: "consultation" },
            email_questions: { text: "Прислать список вопросов на Email", style: "secondary", value: "email_questions" },
            chat_here: { text: "Задать еще вопрос здесь", style: "tertiary", value: "chat_here" }
        },
        // --- [НОВАЯ ФУНКЦИЯ v25.0] ---
        // Фраза-триггер для календаря (должна совпадать с будущим промптом v1.30)
        askDatePrompt: "Отлично! Чтобы помочь вам записаться на консультацию, скажите, пожалуйста, на какую дату вы хотели бы запланировать встречу? Пожалуйста, выберите дату в календаре ниже или введите ее.",
        dateSelectedConfirmation: "Выбрана дата: {date}", // Сообщение пользователя после выбора даты
        // --- [КОНЕЦ ИЗМЕНЕНИЯ v25.0] ---
        ai_transition: "Прекрасно. Теперь вы можете задавать свои юридические вопросы.",

        // --- [UX ИСПРАВЛЕНИЕ v25.3] ---
        finalMessage: "Спасибо! Наша команда рассмотрит вашу информацию и свяжется с вами в ближайшее время. Хорошего дня!",
        // --- [КОНЕЦ ИСПРАВЛЕНИЯ v25.3] ---

        validation: {
            invalid_name: "Это не похоже на настоящее имя. Пожалуйста, введите ваше полное имя.",
            invalid_email: "Этот адрес электронной почты выглядит недействительным. Пожалуйста, проверьте и введите его снова.",
            invalid_phone: "Этот номер телефона выглядит недействительным. Пожалуйста, введите корректный номер.",
            ai_error: "Извините, у меня проблемы с подключением. Пожалуйста, попробуйте позже.",
            placeholder_text: "Напишите ваш ответ...",
            placeholder_phone: "+1 (___) ___-____"
        }
    },
    es: {
        operatorName: ['Carlos', 'María', 'Sofía', 'Diego', 'Valentina'][Math.floor(Math.random() * 5)],
        greeting: "Hola, soy {operatorName}. Para comenzar, por favor seleccione su idioma preferido.",
        transition_message: "Un momento, por favor. Le conecto con un especialista.",
        welcome: "¡Bienvenido! Soy {operatorName}. ¿En qué puedo ayudarle hoy?",
        askService: "¿Con qué área legal está relacionada su consulta?",
        services: {
            estate_planning: "Planificación Patrimonial",
            real_estate: "Bienes Raíces",
            other: "Otro Asunto"
        },
        askEstateTopic: "Por supuesto. ¿Le interesa principalmente redactar un Testamento o un Fideicomiso?",
        estateTopics: {
            will: "Testamento",
            trust: "Fideicomiso",
            unsure: "No estoy seguro/a"
        },
        askRealEstateTopic: "Claro. ¿En qué aspecto de bienes raíces puedo ayudarle hoy?",
        realEstateTopics: {
            purchase_sale: "Contratos de Compraventa",
            title_insurance: "Examen de Título y Seguro",
            closing_services: "Servicios de Cierre",
            fsbo: "Venta por el Propietario (FSBO)",
            lease_agreements: "Contratos de Arrendamiento",
            international_clients: "Asesoría para Clientes Internacionales"
        },
        inquiryThanks: "Gracias por aclarar. Para asistirle mejor, necesito recopilar algunos datos.",
        askName: "Primero, ¿cuál es su nombre completo?",
        askEmail: "Gracias, {name}. ¿Cuál es su dirección de correo electrónico?",
        askPhone: "Entendido. Por último, ¿cuál es su número de teléfono?",
        thanksAndNextSteps: "Excelente, {name}. Tengo sus datos iniciales. ¿Qué desea hacer a continuación?",
        nextSteps: {
            consultation: { text: "Agendar Consulta Ahora", style: "primary", value: "consultation" },
            email_questions: { text: "Enviarme Preguntas por Email", style: "secondary", value: "email_questions" },
            chat_here: { text: "Hacer Más Preguntas Aquí", style: "tertiary", value: "chat_here" }
        },
        askDatePrompt: "¡Perfecto! Para ayudarle a agendar una consulta, ¿podría indicarme qué fecha prefiere? Seleccione una fecha del calendario o escríbala.",
        dateSelectedConfirmation: "Fecha seleccionada: {date}",
        ai_transition: "Perfecto. Ahora puede hacerme sus preguntas legales.",
        finalMessage: "¡Gracias! Nuestro equipo revisará su información y se pondrá en contacto con usted en breve. ¡Que tenga un buen día!",
        validation: {
            invalid_name: "Eso no parece un nombre válido. Por favor, ingrese su nombre completo.",
            invalid_email: "Esta dirección de correo parece inválida. Por favor, verifíquela e ingrésela de nuevo.",
            invalid_phone: "Este número de teléfono parece inválido. Por favor, ingrese un número válido.",
            ai_error: "Lo siento, tengo problemas de conexión en este momento. Por favor, intente de nuevo más tarde.",
            placeholder_text: "Escriba su respuesta aquí...",
            placeholder_phone: "+1 (___) ___-____"
        }
    }
};

const DISCLAIMER_TEXT = "Please note: This chat does not create a lawyer-client relationship. By continuing, you agree that your information may be shared with our partner businesses to assist you, and that they may contact you via phone or text. Your consent is not a condition of purchase. Proceeding with the chat signifies your agreement.";

// --- 3. ФУНКЦИИ ВАЛИДАЦИИ ---
const isValidName = (name) => name && name.trim().length > 2 && name.trim().includes(' ');
// [ИЗМЕНЕНИЕ v2.0] Разрешаем любой ввод > 2 символов, чтобы пользователь мог написать "нет email"
const isValidEmail = (email) => email && email.trim().length > 2;
// --- [ИЗМЕНЕНИЕ v25.7] ---
// Валидация телефона теперь проверяет *длину* отформатированной маски
// Убедимся, что +1 и 10 цифр введены
const isValidPhone = (phone) => phone && phone.replace(/[^\d]/g, '').length >= 11;
// --- [КОНЕЦ ИЗМЕНЕНИЯ v25.7] ---


// Helper для паузы
const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));



// 🔴 Разборщик разметки переехал в utils/razmetka.js — держатель ОДИН на
//    оба виджета (quick-260912-4cp). Копии здесь больше нет; санитайзер
//    теперь внутри razmetkaVHtml, снаружи его позвать забыть нельзя.


// [ИСПРАВЛЕНИЕ v25.2] Эта функция больше не используется,
// так как проверка была слишком хрупкой. Мы используем
// явную логику в handleOptionClick.
/*
const isISODateString = (str) => {
    if (!str || typeof str !== 'string') return false;
    // Простая проверка на формат YYYY-MM-DDTHH:MM:SS
    return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(str);
};
*/

// --- 4. ОСНОВНОЙ КОМПОНЕНТ ВИДЖЕТА ---
const PublicChatWidget = () => {
    // [Wave 3] Tenant awareness — html.fitenko cascade re-skins via CSS; this is the explicit hook consumer required by plan acceptance.
    // eslint-disable-next-line no-unused-vars
    const { isFitenko, branding } = useBranding();
    // --- 5. СОСТОЯНИЕ КОМПОНЕНТА (STATE) ---
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [userInfo, setUserInfo] = useState({ fullName: null, email: null, phone: null, service: null, topic: null });
    const [isLoading, setIsLoading] = useState(false);
    const [language, setLanguage] = useState('en');
    const [conversationState, setConversationState] = useState('start');
    const [isDisclaimerVisible, setIsDisclaimerVisible] = useState(true);
    // --- [НОВАЯ ФУНКЦИЯ v25.0] ---
    const [showCalendar, setShowCalendar] = useState(false); // State для календаря
    // --- [КОНЕЦ ИЗМЕНЕНИЯ v25.0] ---
    const [isFirstMessage, setIsFirstMessage] = useState(true);

    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const [currentStrings, setCurrentStrings] = useState(translations.en);

    // (Шаг 3.2): Читаем параметры из URL
    const urlParams = new URLSearchParams(window.location.search);
    const disableAutoOpen = urlParams.get('disableAutoOpen') === 'true';
    const langParam = urlParams.get('lang');
    // [NEW] ?languages=en,ru override
    const languagesParam = urlParams.get('languages');

    // [NEW] Enabled languages config (fetched from backend)
    const [enabledLanguages, setEnabledLanguages] = useState({ en: true, ru: true, es: true });

    // --- 6. ЭФФЕКТЫ (USEEFFECT) --- (Развернутый вид)

    // [NEW] Fetch enabled languages from backend on mount
    useEffect(() => {
        // URL param ?languages=en,ru overrides backend config
        if (languagesParam) {
            const paramLangs = languagesParam.split(',').map(l => l.trim().toLowerCase());
            setEnabledLanguages({ en: paramLangs.includes('en'), ru: paramLangs.includes('ru'), es: paramLangs.includes('es') });
            return;
        }
        apiService.makeRequest('/voice/public/widget-config', {}, true)
            .then(data => {
                if (data && data.enabled_languages) {
                    setEnabledLanguages(data.enabled_languages);
                }
            })
            .catch(err => {
                console.warn('[Widget] Could not fetch widget config, using defaults.', err);
            });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // [NEW] Opt-in dark theme for embeds on dark-themed host sites (e.g. estatelawbrooklyn.com).
    // Toggled via ?theme=dark; styling lives in the `html.embed-dark` block of the CSS.
    // Additive + gated → MLG/Fitenko embeds (no theme param) keep their palette.
    useEffect(() => {
        if (urlParams.get('theme') === 'dark') {
            document.documentElement.classList.add('embed-dark');
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // [eln] Force the assistant's display name to "Sofia" (the eln brand) instead of
    // the random operator pool. Instance-gated: Fitenko/mlg keep their random names.
    useEffect(() => {
        if (branding?.instance_name !== 'eln') return;
        translations.en.operatorName = 'Sofia';
        translations.ru.operatorName = 'София';
        translations.es.operatorName = 'Sofía';
        setCurrentStrings(prev => ({ ...prev, operatorName: translations[language]?.operatorName || 'Sofia' }));
    }, [branding, language]); // eslint-disable-line react-hooks/exhaustive-deps

    // (Шаг 3.2): Условное авто-открытие
    useEffect(() => {
        const isInIframe = window.self !== window.top;
        if (!isInIframe || !disableAutoOpen) {
            const autoOpenTimer = setTimeout(() => {
                if (!isOpen) setIsOpen(true);
            }, 30000);
            return () => clearTimeout(autoOpenTimer);
        }
    }, [isOpen, disableAutoOpen]);

    // Авто-скролл
    useEffect(() => {
        setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
    }, [messages]);

    // Инициализация
    useEffect(() => {
        if (isOpen && messages.length === 0) {
            handleConversationFlow();
        }
    }, [isOpen]);

    // Авто-фокус
    useEffect(() => {
        const isCollectingInfo = (conversationState === 'collecting_info' && userInfo.topic && !userInfo.phone);
        const isAiChat = conversationState === 'ai_chat';
        // --- [НОВАЯ ФУНКЦИЯ v25.0] ---
        // Не фокусируем инпут, если показываем календарь
        if (inputRef.current && !showCalendar && (isCollectingInfo || isAiChat)) {
            // --- [КОНЕЦ ИЗМЕНЕНИЯ v25.0] ---
            setTimeout(() => {
                inputRef.current?.focus();
            }, 0);
        }
        // --- [НОВАЯ ФУНКЦИЯ v25.0] Добавляем showCalendar в зависимости ---
    }, [messages, conversationState, isLoading, userInfo.topic, userInfo.phone, showCalendar]);
    // --- [КОНЕЦ ИЗМЕНЕНИЯ v25.0] ---


    // (Шаг 3.3): Обработка команд от родительского окна
    useEffect(() => {
        const handleParentMessage = (event) => {
            const { type, payload } = event.data;
            switch (type) {
                case 'prefill-user-data':
                    setUserInfo(prev => ({ ...prev, ...payload }));
                    break;
                case 'open':
                    setIsOpen(true);
                    break;
                case 'close':
                    setIsOpen(false);
                    break;
                case 'set-language':
                    if (payload.language) {
                        setLanguage(payload.language);
                        if (translations[payload.language]) {
                            setCurrentStrings(translations[payload.language]);
                        } else {
                            setCurrentStrings(translations.en);
                        }
                    }
                    break;
                default:
                    break;
            }
        };
        window.addEventListener('message', handleParentMessage);
        return () => {
            window.removeEventListener('message', handleParentMessage);
        };
    }, []);

    // (Шаг 3.4): Уведомляем о готовности
    useEffect(() => {
        sendMessageToParent('widget-ready');
    }, []);

    // (Шаг 3.2, часть 2): Применить язык из URL
    useEffect(() => {
        if (langParam && translations[langParam]) {
            setLanguage(langParam);
            if (langParam !== 'en') {
                setCurrentStrings(translations[langParam]);
            }
        }
    }, [langParam]);

    // --- 7. ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ ---

    // [ИЗМЕНЕНИЕ v22] Управляем isLoading снаружи
    const addBotMessage = (text, options = [], operatorName = null) => {
        return new Promise((resolve) => {
            const baseDelay = 800;
            const charDelay = 30;
            const maxDelay = 3500;
            let calculatedDelay = baseDelay + (text.length * charDelay);
            if (options.length > 0) {
                calculatedDelay += 500;
            }
            const delay = Math.min(calculatedDelay, maxDelay);

            setTimeout(() => {
                const operator = operatorName || currentStrings.operatorName;
                setMessages(prev => [...prev, { text, sender: 'ai', operator: operator, options }]);
                resolve();
            }, delay);
        });
    };

    const submitLead = async (info) => {
        try {
            // --- [ИЗМЕНЕНИЕ v25.7] ---
            // Очищаем номер от маски перед отправкой в API
            const cleanPhone = info.phone ? info.phone.replace(/[^\d+]/g, '') : '';

            // [ИЗМЕНЕНИЕ v2.0] Если email не похож на email (например "no email"), отправляем null
            let cleanEmail = info.email;
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (cleanEmail && !emailRegex.test(cleanEmail)) {
                cleanEmail = null;
            }

            const payload = { ...info, email: cleanEmail, language: language, phone: cleanPhone };

            await apiService.createLead(payload);
            // --- [КОНЕЦ ИЗМЕНЕНИЯ v25.7] ---

            // --- [ЗАДАЧА 40 / v25.5] ИСПРАВЛЕНИЕ (Баг 2) ---
            // toast.success(currentStrings.finalMessage);
            // --- [КОНЕЦ ИСПРАВЛЕНИЯ v25.5] ---

            sendMessageToParent('lead-submitted', payload); // [v25.7] Отправляем payload с очищенным номером
        } catch (error) {
            toast.error('Could not save your information.');
            console.error("Lead creation failed:", error);
        }
    };

    // --- 8. ГЛАВНАЯ ЛОГИКА: СЦЕНАРИЙ ДИАЛОГА --- (v22 - async/await + try/finally)
    const handleConversationFlow = async (userResponse = null) => {
        setIsLoading(true);
        try {
            switch (conversationState) {
                case 'start': {
                    const allLanguageOptions = [
                        { text: "English", value: 'en' },
                        { text: "Русский", value: 'ru' },
                        { text: "Español", value: 'es' },
                    ];
                    const activeLanguageOptions = allLanguageOptions.filter(opt => enabledLanguages[opt.value] !== false);

                    // Skip language selection if only 1 language is enabled
                    if (activeLanguageOptions.length === 1) {
                        const onlyLang = activeLanguageOptions[0].value;
                        const onlyStrings = translations[onlyLang] || translations.en;
                        setLanguage(onlyLang);
                        setCurrentStrings(onlyStrings);
                        const anonEmail = `anon_${Date.now()}_${Math.floor(Math.random()*1000)}@katifi.local`;
                        setUserInfo(prev => ({ ...prev, email: anonEmail, fullName: "Anonymous Visitor" }));
                        setConversationState('ai_chat');
                        await addBotMessage(
                            onlyStrings.welcome.replace('{operatorName}', onlyStrings.operatorName),
                            [],
                            onlyStrings.operatorName
                        );
                    } else {
                        setConversationState('selecting_language');
                        await addBotMessage(currentStrings.greeting.replace('{operatorName}', currentStrings.operatorName), activeLanguageOptions);
                    }
                    break;
                }

                case 'selecting_language':
                    const newLang = userResponse.value;
                    const newStrings = translations[newLang] || translations.en;
                    const newOperatorName = newStrings.operatorName;
                    const initialOperatorName = messages[0]?.operator;

                    setLanguage(newLang);
                    setCurrentStrings(newStrings);
                    
                    // Generate an anonymous lead session ID
                    const anonEmail = `anon_${Date.now()}_${Math.floor(Math.random()*1000)}@katifi.local`;
                    setUserInfo(prev => ({ ...prev, email: anonEmail, fullName: "Anonymous Visitor" }));
                    setConversationState('ai_chat');

                    if (newLang === 'ru') {
                        await addBotMessage(
                            newStrings.transition_message,
                            [],
                            initialOperatorName
                        );
                        await wait(1800);
                    }

                    await addBotMessage(
                        newStrings.welcome.replace('{operatorName}', newOperatorName),
                        [],
                        newOperatorName
                    );
                    break;
                default:
                    break;
            }
        } finally {
            setIsLoading(false);
        }
    };

    // --- [НОВАЯ ФУНКЦИЯ v2.1] Human Takeover ---
    const [isHumanMode, setIsHumanMode] = useState(false);
    const [lastSyncId, setLastSyncId] = useState(0);

    // Polling Effect for Human Mode
    useEffect(() => {
        if (!isHumanMode || !userInfo.email) return;

        const pollInterval = setInterval(async () => {
            try {
                const response = await apiService.syncChatMessages(userInfo.email, lastSyncId);

                // Check if mode changed back to AI
                if (response.status === 'ai' && isHumanMode) {
                    setIsHumanMode(false);
                    return;
                }

                if (response.messages && response.messages.length > 0) {
                    let maxId = lastSyncId;
                    response.messages.forEach(msg => {
                        maxId = Math.max(maxId, msg.id);
                        // Only add AI/Operator messages that we haven't seen
                        // (User messages are added locally immediately)
                        if (msg.role === 'assistant') {
                            setMessages(prev => [...prev, {
                                text: msg.content,
                                sender: 'ai',
                                operator: currentStrings.operatorName + " (Human)", // Indicate human? Or keep seamless?
                                options: []
                            }]);
                        }
                    });
                    setLastSyncId(maxId);
                }
            } catch (error) {
                console.error("Polling error:", error);
            }
        }, 3000); // Poll every 3 seconds

        return () => clearInterval(pollInterval);
    }, [isHumanMode, userInfo.email, lastSyncId]);

    // --- [ЗАДАЧА 41 / v25.6] ИСПРАВЛЕННАЯ ФУНКЦИЯ ---
    const sendAiChatMessage = async (messageText) => {
        setIsLoading(true);
        setShowCalendar(false); // Скрываем календарь в начале
        try {
            // --- [ИЗМЕНЕНИЕ v25.7] ---
            // Очищаем номер от маски, если пользователь ввел его вручную
            let finalMessageText = messageText;
            if (messageText.startsWith('+1 (')) {
                finalMessageText = messageText.replace(/[^\d+]/g, '');
            }

            const payload = {
                message: finalMessageText, // Отправляем очищенное сообщение
                // --- [КОНЕЦ ИЗМЕНЕНИЯ v25.7] ---
                email: userInfo.email,
                fullName: userInfo.fullName,
                phone: userInfo.phone ? userInfo.phone.replace(/[^\d+]/g, '') : '',
                topic: userInfo.topic,
                language: language
            };
            console.debug("[Widget] Sending payload to AI:", payload);

            let response;
            if (isFirstMessage) {
                // Первый запрос всегда идет на /public/chat для инициализации Lead-профиля
                const firstMsgPayload = {
                    email: userInfo.email,
                    message: finalMessageText,
                    full_name: userInfo.fullName,
                    language: language
                };
                response = await apiService.handlePublicChatMessage(firstMsgPayload);
                setIsFirstMessage(false);
            } else {
                response = await apiService.publicChatWithAI(payload);
            }

            // --- [НОВАЯ ФУНКЦИЯ v2.1] Human Mode Check ---
            if (response.status === 'human_mode') {
                console.log("[Widget] Human Takeover Detected. Switching to Polling.");
                setIsHumanMode(true);
                setIsLoading(false);
                return; // Stop processing AI logic
            } else {
                if (isHumanMode) setIsHumanMode(false); // Switch back if backend says so
            }
            // ---------------------------------------------

            await wait(1200);

            const aiResponseText = response.ai_response;
            let aiResponseOptions = response.options || []; // <--- [v25.6] Изменено на let

            console.log("[Widget] Received AI Response Text:", aiResponseText);

            // --- [НОВОЕ ИСПРАВЛЕНИЕ v25.6] ---
            // Проверяем, нужно ли повторно показать кнопки после отправки Email.
            const lowerCaseText = aiResponseText ? aiResponseText.toLowerCase() : "";
            const nextStepsTriggerEn = "what would you like to do next?";
            // AI (v1.54) должен ответить на языке пользователя.
            // --- [ИЗМЕНЕНИЕ v25.7] ---
            // Уточняем русскую фразу-триггер
            const nextStepsTriggerRu = "чем могу помочь дальше?";
            // --- [КОНЕЦ ИЗМЕНЕНИЯ v25.7] ---

            if (
                (lowerCaseText.includes(nextStepsTriggerEn) || lowerCaseText.includes(nextStepsTriggerRu)) &&
                aiResponseOptions.length === 0 // Убедимся, что AI сам не прислал кнопки
            ) {
                console.log("[Widget] '...what would you like to do next?' trigger found. Adding buttons.");

                // Получаем кнопки из переводов
                const scheduleBtn = currentStrings.nextSteps.consultation;
                const chatBtn = currentStrings.nextSteps.chat_here;

                // Добавляем только "Schedule" и "Ask More" по запросу
                aiResponseOptions = [
                    { text: scheduleBtn.text, value: scheduleBtn.value, style: scheduleBtn.style },
                    { text: chatBtn.text, value: chatBtn.text, style: chatBtn.style } // [v25.7] Исправлена опечатка value
                ];
            }
            // --- [КОНЕЦ ИСПРАВЛЕНИЯ v25.6] ---


            const aiMessage = {
                text: aiResponseText,
                sender: 'ai',
                operator: currentStrings.operatorName,
                isHtml: true,
                // [v25.6] Используем .map() для гарантии формата,
                // даже если мы только что создали массив
                options: aiResponseOptions.map(opt => ({
                    text: opt.text,
                    value: opt.value,
                    style: opt.style
                }))
            };

            setMessages(prev => [...prev, aiMessage]);

            // Проверяем, нужно ли показать календарь после ответа AI
            // (lowerCaseText уже определен выше)
            const triggerPhraseEn = "select a date from the calendar";
            const triggerPhraseRu = "выберите дату в календаре";

            console.log("[Widget] Checking for calendar trigger phrases in:", lowerCaseText);
            console.log(`[Widget] Checking EN phrase: "${triggerPhraseEn}" - Found:`, lowerCaseText.includes(triggerPhraseEn));
            console.log(`[Widget] Checking RU phrase: "${triggerPhraseRu}" - Found:`, lowerCaseText.includes(triggerPhraseRu));

            if (lowerCaseText.includes(triggerPhraseEn) || lowerCaseText.includes(triggerPhraseRu)) {
                console.log("[Widget] !!! TRIGGER PHRASE FOUND! Setting showCalendar to true !!!");
                setShowCalendar(true);
            } else {
                console.log("[Widget] Trigger phrase NOT found.");
            }

        } catch (error) {
            console.error("AI chat error:", error);
            toast.error(error.message || currentStrings.validation.ai_error);
            const errorMessage = { text: currentStrings.validation.ai_error, sender: 'ai', operator: currentStrings.operatorName };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };
    // --- [КОНЕЦ ИЗМЕНЕНИЯ v25.6] ---

    // --- 9. ОБРАБОТЧИКИ СОБЫТИЙ ---

    const handleDismissDisclaimer = () => {
        setIsDisclaimerVisible(false);
    };

    const handleToggle = () => {
        const newState = !isOpen;
        setIsOpen(newState);
        sendMessageToParent(newState ? 'widget-opened' : 'widget-closed');
    };

    // --- [НОВАЯ ФУНКЦИЯ v25.0] ---
    // Обработчик выбора даты в календаре
    const handleDateSelect = async (date) => {
        if (!date || isLoading) return;

        // 1. Форматируем дату
        const formattedDate = formatDate(date, 'yyyy-MM-dd');
        // Используем более универсальный формат для отображения пользователю
        const userFriendlyDate = formatDate(date, 'MMM do, yyyy'); // e.g., Oct 29th, 2025

        // 2. Создаем специальное сообщение для бэкенда
        const messageToSendToBackend = `date_selected:${formattedDate}`;

        // 3. Добавляем сообщение пользователя в чат
        const userMessageText = currentStrings.dateSelectedConfirmation.replace('{date}', userFriendlyDate);
        setMessages(prev => [...prev, { text: userMessageText, sender: 'user' }]);

        // 4. Отправляем специальное сообщение на бэкенд
        console.debug(`[Widget] Sending selected date: ${messageToSendToBackend}`);
        await sendAiChatMessage(messageToSendToBackend); // sendAiChatMessage скроет календарь

        // 5. Календарь уже скрыт внутри sendAiChatMessage
        // setShowCalendar(false);
    };
    // --- [КОНЕЦ ИЗМЕНЕНИЯ v25.0] ---

    // --- [ЗАДАЧА 40 / v25.5] ИСПРАВЛЕННАЯ ФУНКЦИЯ (Баг 1) ---
    const handleOptionClick = async (option) => {
        if (option.disabled || isLoading) return;

        // 1. Добавляем сообщение пользователя в UI (используем .text для отображения)
        setMessages(prev => [...prev, { text: option.text, sender: 'user' }]);

        // 2. Определяем, находимся ли мы на последнем шаге сбора инфо
        const isFinalInfoStep = (conversationState === 'collecting_info' && userInfo.fullName && userInfo.email && userInfo.phone);
        let messageToSendToAI;

        // СЦЕНАРИЙ 1: Мы находимся в 'ai_chat' (т.е. после выбора даты, ожидаем выбор слота)
        if (conversationState === 'ai_chat') {

            // СЦЕНАРИЙ 1A: Пользователь нажал "Выбрать другую дату"
            if (option.value === 'show_other_dates') {
                console.debug("[Widget] User clicked 'show_other_dates'.");
                setIsLoading(true);
                // Повторно показываем приглашение выбрать дату и календарь
                await addBotMessage(currentStrings.askDatePrompt);
                setShowCalendar(true);
                setIsLoading(false);

                // СЦЕНАРИЙ 1B: Пользователь нажал на СЛОТ ВРЕМЕНИ (любое другое значение)
            } else {
                // Мы предполагаем, что любое другое 'value' в этом состоянии - это ISO-строка
                messageToSendToAI = option.value; // <-- ОТПРАВЛЯЕМ VALUE
                console.debug(`[Widget] Sending time slot (option.value): ${messageToSendToAI}`);
                // Отправляем ISO-строку (или что там в value) в бэкенд
                await sendAiChatMessage(messageToSendToAI);
            }

            // СЦЕНАРИЙ 2: Мы находимся на последнем шаге сбора информации (кнопки "Schedule", "Email Me", "Chat Here")
        } else if (isFinalInfoStep && ['consultation', 'email_questions', 'chat_here'].includes(option.value)) {

            setConversationState('ai_chat'); // Переходим в 'ai_chat'

            // --- [ЗАДАЧА 40 / v25.5] Глобальное исправление.
            // Мы должны ОТПРАВИТЬ ТЕКСТ КНОПКИ в AI, а не иметь "умную" логику на фронте.
            // "Умная" логика (v25.4) вызвала рассинхронизацию с бэкендом (v1.54).

            // "Chat Here" - единственный, у кого есть особая логика (просто переход в режим чата)
            if (option.value === 'chat_here') {
                setIsLoading(true);
                await addBotMessage(currentStrings.ai_transition);
                setIsLoading(false);
            } else {
                // ДЛЯ 'consultation' И 'email_questions':
                // Мы отправляем .text (то, что видит пользователь), а не .value
                // Бэкенд v1.54 сам знает, как на это реагировать (показать календарь или отправить email).
                messageToSendToAI = option.text;
                console.debug(`[Widget] Sending button text to AI: ${messageToSendToAI}`);
                await sendAiChatMessage(messageToSendToAI);
            }
            // --- [КОНЕЦ ИСПРАВЛЕНИЯ v25.5] ---

            // СЦЕНАРИЙ 3: Мы находимся в начальном потоке (выбор языка, темы и т.д.)
        } else {
            await handleConversationFlow(option);
        }
    };
    // --- [КОНЕЦ ИСПРАВЛЕНИЯ v25.5] ---


    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!input.trim() || isLoading) return;

        const userMessageText = input;
        // Add user message to UI
        setMessages(prev => [...prev, { text: userMessageText, sender: 'user' }]);

        // --- [НОВАЯ ФУНКЦИЯ v25.0] ---
        // Hide calendar if user types instead of selecting date
        setShowCalendar(false);
        // --- [КОНЕЦ ИЗМЕНЕНИЯ v25.0] ---


        // Clear input immediately for AI chat, otherwise wait for validation in handleConversationFlow
        if (conversationState === 'ai_chat') {
            setInput('');
            await sendAiChatMessage(userMessageText);
        } else {
            // handleConversationFlow will read 'input' and clear it only on successful validation
            await handleConversationFlow();
        }
    };

    // --- 10. JSX-РАЗМЕТКА ---

    // --- [ИЗМЕНЕНИЕ v25.7] ---
    // Определяем, какой тип ввода мы ожидаем, для маски и type="email"
    const isEmailInput = (conversationState === 'collecting_info' && userInfo.fullName && !userInfo.email);
    const isPhoneInput = (conversationState === 'collecting_info' && userInfo.email && !userInfo.phone);
    // --- [КОНЕЦ ИЗМЕНЕНИЯ v25.7] ---

    // --- [НОВАЯ ФУНКЦИЯ v25.0] ---
    // Hide input form if calendar is shown
    const showInputForm = !showCalendar && (
        (conversationState === 'collecting_info' && userInfo.topic && !userInfo.phone) ||
        conversationState === 'ai_chat'
    );
    // --- [КОНЕЦ ИЗМЕНЕНИЯ v25.0] ---

    return (
        <>
            {!isOpen && (
                <button className="chat-fab" onClick={handleToggle}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="white">
                        <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-3.5 11c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm-4.5 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm-4.5 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z" />
                    </svg>
                </button>
            )}
            {isOpen && (
                <div className="public-chat-widget">
                    <div className="chat-widget-header">
                        <h3>{currentStrings.operatorName} | Legal Assistant</h3>
                        <button className="close-btn" onClick={handleToggle}>&times;</button>
                    </div>
                    <div className="chat-widget-body">
                        {/* Disclaimer */}
                        {isDisclaimerVisible && (
                            <div className="disclaimer">
                                <div className="disclaimer-content">
                                    <p>{DISCLAIMER_TEXT}</p>
                                </div>
                                <button
                                    className="disclaimer-close-btn"
                                    onClick={handleDismissDisclaimer}
                                    aria-label="Dismiss disclaimer"
                                >
                                    &times;
                                </button>
                            </div>
                        )}

                        <div className="messages">
                            {messages.map((msg, index) => (
                                <div key={index} className={`message ${msg.sender}`}>
                                    {msg.sender === 'ai' && <div className="operator-name">{msg.operator}</div>}
                                    {/* Render message content */}
                                    {msg.isHtml ? (
                                        <div className="message-content" dangerouslySetInnerHTML={{ __html: razmetkaVHtml(msg.text) }} />
                                    ) : (
                                        <div className="message-content">{msg.text}</div>
                                    )}

                                    {/* Render option buttons */}
                                    {/* Condition remains: show only for the last message */}
                                    {msg.options && index === messages.length - 1 && (
                                        <div className="message-options">
                                            {msg.options.map((opt, i) => (
                                                <button
                                                    key={opt.value || i}
                                                    onClick={() => handleOptionClick(opt)}
                                                    disabled={opt.disabled || isLoading} // Also disable buttons when loading
                                                    className={`option-btn-${opt.style || 'secondary'}`}
                                                >
                                                    {opt.text}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                            {/* Loading Indicator */}
                            {isLoading && (
                                <div className="message ai typing-indicator">
                                    <span></span><span></span><span></span>
                                </div>
                            )}
                            <div ref={messagesEndRef} /> {/* For auto-scrolling */}
                        </div>

                        {/* --- [НОВАЯ ФУНКЦИЯ v25.0] --- */}
                        {/* Render Calendar */}
                        {showCalendar && !isLoading && (
                            <div className="calendar-container">
                                <DatePicker
                                    selected={null} // We don't need to keep the selection highlighted here
                                    onChange={handleDateSelect}
                                    minDate={new Date()} // Disable past dates
                                    inline // Embed the calendar
                                // locale="ru" // Uncomment and import locale if needed: import { ru } from 'date-fns/locale';
                                />
                            </div>
                        )}
                        {/* --- [КОНЕЦ ИЗМЕНЕНИЯ v25.0] --- */}


                        {/* Input Form */}
                        {showInputForm && (
                            <form onSubmit={handleSendMessage} className="chat-form">

                                {/* --- [ИСПРАВЛЕНИЕ v25.7] --- */}
                                {/* Используем <InputMask> если это ввод телефона, иначе обычный <input> */}
                                {isPhoneInput ? (
                                    <InputMask
                                        mask="+1 (999) 999-9999"
                                        maskChar={null} // Не показывать "_"
                                        value={input}
                                        onChange={(e) => setInput(e.target.value)}
                                        disabled={isLoading}
                                    >
                                        {(inputProps) => (
                                            <input
                                                {...inputProps}
                                                ref={inputRef}
                                                type="tel" // 'tel' - семантически верно
                                                placeholder={currentStrings.validation.placeholder_phone}
                                            />
                                        )}
                                    </InputMask>
                                ) : (
                                    <input
                                        ref={inputRef}
                                        type="text" // [ИЗМЕНЕНИЕ v2.0] Всегда text, чтобы не блокировать "no email"
                                        value={input}
                                        onChange={(e) => setInput(e.target.value)}
                                        placeholder={currentStrings.validation.placeholder_text}
                                        disabled={isLoading}
                                    />
                                )}
                                {/* --- [КОНЕЦ ИСПРАВЛЕНИЯ v25.7] --- */}

                                <button
                                    type="submit"
                                    className="btn-primary"
                                    disabled={isLoading || !input.trim()} // Disable send if loading or input empty
                                >
                                    Send
                                </button>
                            </form>
                        )}
                    </div> {/* chat-widget-body */}
                </div> /* public-chat-widget */
            )}
        </>
    );
};

export default PublicChatWidget;